import { useParams, useNavigate, useLocation, Link, Navigate } from 'react-router-dom';
import { getPostBySlug, getRelatedPosts } from '@/mocks/blogData';
import { retiredBlogSlugs } from '@/mocks/legacyRedirects';
import { blogKnowledgeLinks } from '@/mocks/blogKnowledgeLinks';
import useSeo, { SITE_URL } from '@/hooks/useSeo';
import BlogCard from './BlogCard';
import Breadcrumbs from './Breadcrumbs';
import KnowledgeLinkBox from './KnowledgeLinkBox';
import Navbar from '@/pages/home/components/Navbar';
import FooterSection from '@/pages/home/components/FooterSection';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const post = getPostBySlug(slug || '');

  // Old URL for a renamed article: forward to the live slug instead of showing
  // "Post Not Found". netlify.toml handles this with a real 301 at the server;
  // this covers every other host and in-app navigations.
  const normalizedSlug = (slug || '').trim().toLowerCase().replace(/\/+$/, '');
  const legacyTarget = post ? undefined : retiredBlogSlugs[normalizedSlug];

  const relatedPosts = post ? getRelatedPosts(post.slug, 3) : [];
  const knowledgeLink = slug ? blogKnowledgeLinks[slug] : undefined;

  const postUrl = `${SITE_URL}/blog/${slug || ''}`;

  useSeo({
    title: post ? `${post.title} | TNT Coach AI Blog` : 'Post Not Found | TNT Coach AI Blog',
    description: post
      ? post.excerpt
      : 'The article you are looking for does not exist or has been moved. Browse the TNT Coach AI blog for coaching insights on mindset, productivity, and personal growth.',
    // A missing article must never claim a canonical or an indexable status —
    // that is what turns a 404 into a soft 404.
    canonical: post ? postUrl : undefined,
    url: postUrl,
    type: 'article',
    image: post ? post.coverImage : undefined,
    noindex: !post,
  });

  if (!post && legacyTarget) {
    return <Navigate to={`/blog/${legacyTarget}${location.hash}`} replace />;
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-midnight">
        <Navbar />
        <main className="pt-32 pb-16 px-4 md:px-8 lg:px-12 text-center">
          <div className="max-w-2xl mx-auto">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-royal-navy/30 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-3xl text-silver-grey" />
            </div>
            <h1 className="text-3xl font-heading font-bold text-warm-white mb-3">Post Not Found</h1>
            <p className="text-silver-grey mb-6">
              The article you are looking for does not exist or has been moved. It may have been
              renamed — the full library is still one click away.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                onClick={() => navigate('/blog')}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full gold-gradient text-midnight font-semibold text-sm hover:opacity-90 transition-opacity duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line" />
                Back to Blog
              </button>
              <Link
                to="/knowledge"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-royal-navy/40 text-silver-grey text-sm font-medium hover:text-warm-white hover:border-champagne-gold/40 transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-open-line" />
                Browse the Knowledge Hub
              </Link>
            </div>
          </div>
        </main>
        <FooterSection />
      </div>
    );
  }

  const postStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    image: post.coverImage,
    datePublished: post.publishedAt,
    dateModified: post.publishedAt,
    author: {
      '@type': 'Organization',
      name: post.author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'TNT Coach AI',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': typeof window !== 'undefined' ? window.location.href : '',
    },
    keywords: post.tags.join(', '),
    ...(knowledgeLink
      ? {
          about: {
            '@type': 'Thing',
            name: knowledgeLink.topicLabel,
            '@id': `${SITE_URL}/knowledge#${knowledgeLink.topicId}`,
          },
        }
      : {}),
  };

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Blog', href: '/blog' },
    { label: post.title },
  ];

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${SITE_URL}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: `${SITE_URL}/blog`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: post.title,
        item: postUrl,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-midnight">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(postStructuredData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbStructuredData) }}
      />
      <Navbar />

      <main>
        <article>
          <header className="relative pt-32 pb-12 px-4 md:px-8 lg:px-12">
            <div className="max-w-3xl mx-auto">
              <Breadcrumbs items={breadcrumbItems} />

              <div className="flex items-center gap-3 mb-4">
                <span className="px-2.5 py-0.5 rounded-full bg-royal-navy/30 text-silver-grey text-xs font-medium">
                  {post.category}
                </span>
                <span className="text-taupe-grey text-xs">{post.readTime}</span>
                <span className="text-taupe-grey text-xs">{post.publishedAt}</span>
              </div>

              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-warm-white mb-4 leading-tight">
                {post.title}
              </h1>

              <p className="text-silver-grey text-lg leading-relaxed mb-6">
                {post.excerpt}
              </p>

              <div className="flex items-center gap-3 text-sm text-silver-grey">
                <div className="w-8 h-8 rounded-full gold-gradient flex items-center justify-center text-midnight font-bold text-xs">
                  T
                </div>
                <span>{post.author}</span>
              </div>
            </div>
          </header>

          <div className="px-4 md:px-8 lg:px-12 pb-8">
            <div className="max-w-3xl mx-auto">
              <div className="relative overflow-hidden rounded-lg mb-10 aspect-[21/9]">
                <img
                  src={post.coverImage}
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose prose-invert prose-gold max-w-none
                  prose-headings:font-heading prose-headings:text-warm-white prose-headings:font-semibold
                  prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                  prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                  prose-p:text-silver-grey prose-p:leading-relaxed prose-p:mb-4
                  prose-strong:text-warm-white prose-strong:font-semibold
                  prose-em:text-champagne-gold
                  [&_h2]:font-heading [&_h2]:text-warm-white [&_h2]:font-semibold [&_h2]:text-2xl [&_h2]:mt-10 [&_h2]:mb-4
                  [&_h3]:font-heading [&_h3]:text-warm-white [&_h3]:font-semibold [&_h3]:text-xl [&_h3]:mt-8 [&_h3]:mb-3
                  [&_p]:text-silver-grey [&_p]:leading-relaxed [&_p]:mb-4
                  [&_strong]:text-warm-white [&_strong]:font-semibold
                  [&_em]:text-champagne-gold
                "
                dangerouslySetInnerHTML={{ __html: post.content }}
              />

              {knowledgeLink && (
                <aside className="mt-8 rounded-lg border-l-2 border-champagne-gold/60 bg-royal-navy/15 px-5 py-4">
                  <p className="text-silver-grey text-sm leading-relaxed">
                    <i className="ri-book-open-line text-champagne-gold mr-2" />
                    <strong className="text-warm-white font-semibold">Deeper on this: </strong>
                    {knowledgeLink.contextLine}{' '}
                    <Link
                      to={`/knowledge#${knowledgeLink.topicId}`}
                      className="text-champagne-gold underline underline-offset-4 decoration-champagne-gold/40 hover:decoration-champagne-gold transition-colors duration-300 whitespace-nowrap"
                    >
                      {knowledgeLink.topicLabel}
                    </Link>
                  </p>
                </aside>
              )}

              <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-royal-navy/30">
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-royal-navy/20 text-silver-grey text-xs border border-royal-navy/30"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              {knowledgeLink && <KnowledgeLinkBox link={knowledgeLink} />}
            </div>
          </div>
        </article>

        {relatedPosts.length > 0 && (
          <section className="py-14 px-4 md:px-8 lg:px-12 bg-royal-navy/5 border-t border-royal-navy/20">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-2xl font-heading font-semibold text-warm-white mb-8">Related Articles</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedPosts.map((rp) => (
                  <BlogCard key={rp.id} post={rp} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <FooterSection />
    </div>
  );
}